import { useGlobalQueries } from './use-global-queries';
import { useLocalization } from './use-localization';

type BusinessVertical = 'B2C' | 'B2B';

export type GenericEvent = {
    event: string;
    [key: string]: string | number;
};

export enum ConsumerApplicationProduct {
    DEPOSIT = 'Deposit',
    CREDIT_CARD = 'Credit Card',
    BRANDED_CONSUMER_LOAN = 'Loans',
}

type ConsumerApplicationInterface = GenericEvent & {
    ApplicationID?: string | number; // Add the ID generated when the user starts their application (if available)
    ProductVariant: string; // Add the exact type of the product e.g. Saving max
    StepName?: string; // Add what page the user is on e.g. 'log in', 'personal info page'
    StepNumber?: string | number; // Add which stage in the application process the user is, e.g. page 1
    BusinessVertical?: BusinessVertical;
};

type ParamsType = {
    businessVertical?: BusinessVertical;
    usePageProductData?: boolean;
};

export const useDataLayer = () => {
    const { getCountry } = useLocalization();
    const { getMarketType, pageQuery } = useGlobalQueries();

    function getPageProductData() {
        const productData: { Product?: string; ProductVariant?: string } = {};

        if (pageQuery.data?.gtm?.Product) {
            productData.Product = pageQuery.data.gtm.Product;
        }
        if (pageQuery.data?.gtm?.ProductVariant) {
            productData.ProductVariant = pageQuery.data.gtm.ProductVariant;
        }

        return productData;
    }

    function dataLayerPush(event: GenericEvent | GenericEvent[], params?: ParamsType) {
        const DEFAULTS = {
            Market: getCountry().toUpperCase(),
            BusinessVertical: params?.businessVertical || getMarketType() === 'corporate' ? 'B2B' : 'B2C',
        };

        const events = Array.isArray(event) ? event : [event];

        events.forEach(e => {
            let data = {
                ...DEFAULTS,
                ...e,
            };

            if (params?.usePageProductData === true) {
                data = {
                    ...data,
                    ...getPageProductData(),
                };
            }

            if (typeof window === 'undefined') {
                return;
            }

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(data);
        });
    }

    function consumerApplicationFlowPush(event: ConsumerApplicationInterface, product: keyof typeof ConsumerApplicationProduct = 'CREDIT_CARD') {
        const DEFAULTS = {
            ApplicationID: '',
            StepName: '',
            StepNumber: '',
            Product: ConsumerApplicationProduct[product],
        };

        dataLayerPush({
            ...DEFAULTS,
            ...event,
        });
    }

    return {
        dataLayerPush,
        consumerApplicationFlowPush,
    };
};
